import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import OtpInput from '@/components/functional/OtpInput/OtpInput.tsx';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SearchParams from '@/_constants/SearchParams.ts';

const confirmEmailSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Invalid email. Try again using link from email that you've received." })
    .email('Invalid email. Contact support.'),
  code: z.string().min(1, { message: 'This field is required.' }),
});

type ConfirmEmailSchema = z.infer<typeof confirmEmailSchema>;

const ConfirmEmailPage: FC = () => {
  const [searchParams] = useSearchParams();
  const email = decodeURIComponent(searchParams.get(SearchParams.EMAIL) ?? '');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmEmailSchema>({
    resolver: zodResolver(confirmEmailSchema),
    defaultValues: {
      email: email,
      code: '',
    },
  });
  const { t } = useTranslation();
  const {
    verifyEmailQuery: {
      mutateAsync: verifyEmail,
      isPending: isLoadingVerifyEmail,
      error: errorVerifyEmail,
      isError: isErrorVerifyEmail,
      isSuccess: isSuccessVerifyEmail,
      reset: resetVerifyEmail,
    },
    resendVerificationCodeQuery: {
      mutateAsync: resendVerificationCode,
      isPending: isLoadingVerificationCode,
      error: errorVerificationCode,
      isError: isErrorVerificationCode,
      isSuccess: isSuccessVerificationCode,
    },
  } = useAuthContext();

  const navigate = useNavigate();


  useEffect(() => {
    resetVerifyEmail();
    setShowSuccessMessage(false);
  }, [resetVerifyEmail]);


  useEffect(() => {
    if (isSuccessVerifyEmail && !showSuccessMessage) {
      setShowSuccessMessage(true);
    }
  }, [isSuccessVerifyEmail, showSuccessMessage]);

  const handleVerifyEmail = (data: ConfirmEmailSchema) => {
    void (async () => {
      try {
        if (isLoadingVerifyEmail || !data.email) return;

        await verifyEmail({
          email: data.email,
          code: data.code,
        });

        setTimeout(() => {
          navigate(Routes.LOGIN);
        }, 3000);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleResendCode = () => {
    void (async () => {
      try {
        if (isLoadingVerificationCode || !email) return;

        await resendVerificationCode({
          email: email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-full max-w-[428px] flex flex-col gap-4" onSubmit={handleSubmit(handleVerifyEmail)}>
        <div className="flex flex-col gap-4">
          {showSuccessMessage && <Alert status="success">{t('verifyEmailPage.successInfo')}</Alert>}
          {isSuccessVerificationCode && <Alert status="success">{t('verifyEmailPage.successInfoResendCode')}</Alert>}
          {isErrorVerifyEmail && <Alert status="error">{errorVerifyEmail?.message}</Alert>}
          {isErrorVerificationCode && <Alert status="error">{errorVerificationCode?.message}</Alert>}
          {errors['email'] ? <Alert status="error">{errors['email']?.message}</Alert> : null}
          <h1 className="text-xl sm:text-2xl font-semibold">{t('verifyEmailPage.header')}</h1>
          <Controller
            control={control}
            name="code"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <OtpInput
                placeholder="######"
                error={errors.code?.message}
                onChange={onChange}
                value={value}
                numInputs={6}
              />
            )}
          />
          <div>
            <button type="button" onClick={handleResendCode} className="text-link underline text-sm">
              {t('button.resendCode')}
            </button>
          </div>
          <p className="text-text-secondary text-xs">
            <Trans i18nKey="verifyEmailPage.policyText">
              By continuing, you agree that we create an account for you (unless already created), and accept our
              <Link target="_blank" className="underline" to={import.meta.env.VITE_TERMS_LINK}>
                Terms and Conditions
              </Link>
              <Link target="_blank" className="underline" to={import.meta.env.VITE_POLICY_LINK}>
                Privacy Policy
              </Link>
              .
            </Trans>
          </p>
          <LoadingButton className="mt-2 w-full" loading={isLoadingVerifyEmail} type="submit" color="neutral">
            {t('button.continue')}
          </LoadingButton>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default ConfirmEmailPage;
